// promo.js — single source of truth for promotion + new-item state
// (backlog #1 + #5, built 2026-07-10).
//
// Promo fields live ON the product doc:
//   promoPrice   number  — discounted unit price (null/absent = no promo)
//   promoQtyLimit number|null — cases sellable at promo price (null = no cap)
//   promoSoldQty  number  — cases sold at promo price so far (order txn bumps it)
//   promoEndsAt   Timestamp|null — promo expiry (null = until sold out/ended)
// New-item fields:
//   isNew     boolean
//   newUntil  Timestamp|null — auto-expiry for the New badge (null = manual)
//
// A promo auto-deactivates when the end date passes OR the limit sells out —
// no admin action needed. DESIGN NOTE: the last promo order may overshoot the
// case limit (whole order gets promo price); we accept that rather than split
// one order line into two prices.

function toMillis(v) {
  if (v == null) return null;
  if (typeof v.toMillis === "function") return v.toMillis();
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? null : t;
}

export function isPromoActive(p, now = Date.now()) {
  if (!p || p.promoPrice == null) return false;
  const ends = toMillis(p.promoEndsAt);
  if (ends != null && ends < now) return false;
  if (p.promoQtyLimit != null && (p.promoSoldQty || 0) >= p.promoQtyLimit) return false;
  return true;
}

export function effectivePrice(p, now = Date.now()) {
  return isPromoActive(p, now) ? p.promoPrice : p.basePrice;
}

export function isNewActive(p, now = Date.now()) {
  if (!p || !p.isNew) return false;
  const until = toMillis(p.newUntil);
  return until == null || until >= now;
}

export function promoRemaining(p) {
  if (!p || p.promoQtyLimit == null) return null; // uncapped
  return Math.max(0, p.promoQtyLimit - (p.promoSoldQty || 0));
}

// True only when the promo's END DATE has passed (sell-out does NOT count as
// expired — a cancel can free allocation and re-activate a sold-out promo).
export function isPromoExpired(p, now = Date.now()) {
  const ends = toMillis(p?.promoEndsAt);
  return ends != null && ends < now;
}

// Human label for the PRODUCT's own expiry/best-before date on short-date
// promos (promoProductExpiry) — null when not set.
export function promoExpiryLabel(p) {
  const ms = toMillis(p?.promoProductExpiry);
  if (ms == null) return null;
  return new Date(ms).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" });
}
